// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Sessions Page
// Route: /sessions
// apps/web/src/routes/sessions.tsx
//
// Timeline view of all development sessions. Filter by project, contributor,
// or work item. Shows AI model usage, token consumption, expertise applied.
// Visual timeline with duration bars and session clustering by date.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  EngineTooltip,
  MetricCard,
  LoadingShimmer,
  EmptyState,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useSessions } from '../lib/api/hooks-extended';
import { useProjects } from '../lib/api/projects';
import type { Session } from '../lib/api/types-extended';

// ─── Route Definition ───────────────────────────────────────────────────────

export const Route = createFileRoute('/sessions')({
  component: SessionsPage,
});

// ─── Main Component ─────────────────────────────────────────────────────────

function SessionsPage() {
  const [projectFilter, setProjectFilter] = useState<number | undefined>();
  const { data: sessions, loading } = useSessions(
    projectFilter ? { projectId: projectFilter } : undefined
  );
  const { data: projects } = useProjects();

  // ── Aggregated metrics ─────────────────────────────────────────────────

  const metrics = useMemo(() => {
    if (!sessions) return null;
    const totalMinutes = sessions.reduce((s, ses) => s + ses.durationMinutes, 0);
    const aiSessions = sessions.filter((s) => s.aiModel || s.aiTokensUsed > 0);
    const totalTokens = sessions.reduce((s, ses) => s + ses.aiTokensUsed, 0);
    const totalCost = sessions.reduce((s, ses) => s + ses.aiCost, 0);
    return {
      total: sessions.length,
      totalHours: Math.round(totalMinutes / 60 * 10) / 10,
      aiSessions: aiSessions.length,
      totalTokens,
      totalCost,
      avgDuration: sessions.length > 0 ? Math.round(totalMinutes / sessions.length) : 0,
    };
  }, [sessions]);

  const countTotal = useCounter(metrics?.total ?? 0, 600, 200);
  const countHours = useCounter(metrics?.totalHours ?? 0, 700, 300);
  const countTokens = useCounter(
    metrics ? Math.round(metrics.totalTokens / 1000) : 0,
    800,
    400
  );

  // ── Group sessions by date ─────────────────────────────────────────────

  const grouped = useMemo(() => {
    if (!sessions) return [];
    const groups: Record<string, Session[]> = {};
    for (const s of sessions) {
      const date = new Date(s.startTime).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(s);
    }
    // Sort dates descending
    return Object.entries(groups).sort(
      (a, b) => new Date(b[1][0].startTime).getTime() - new Date(a[1][0].startTime).getTime()
    );
  }, [sessions]);

  if (loading) {
    return (
      <EnginePageShell title="Sessions" subtitle="TIMELINE" icon="🎯" accentColor={C.amber}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[0, 1, 2, 3].map((i) => (
            <GlassPanel key={i} delay={i * 0.08}>
              <LoadingShimmer height={16} width="50%" />
              <div style={{ height: 8 }} />
              <LoadingShimmer height={28} width="40%" />
            </GlassPanel>
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <GlassPanel key={i} delay={0.4 + i * 0.1} style={{ marginBottom: 12 }}>
            <LoadingShimmer height={60} />
          </GlassPanel>
        ))}
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title="Sessions"
      subtitle="TIMELINE"
      icon="🎯"
      accentColor={C.amber}
      statusLabel={`${metrics?.total ?? 0} RECORDED`}
      statusColor={C.amber}
    >
      {/* ── Metric Cards ──────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        <MetricCard label="Total Sessions" value={countTotal} icon="🎯" color={C.amber}
          tooltip={<div>Development sessions logged across all projects.</div>} />
        <MetricCard label="Total Hours" value={countHours} icon="⏱" color={C.cyan}
          subtitle={`${metrics?.avgDuration ?? 0} min avg`}
          tooltip={<div>Total human + AI collaboration time.</div>} />
        <MetricCard label="AI Sessions" value={metrics?.aiSessions ?? 0} icon="🤖" color={C.violet}
          tooltip={<div>Sessions involving AI models (Claude, GPT-4, etc.).</div>} />
        <MetricCard
          label="AI Tokens"
          value={`${countTokens}K`}
          icon="🔤"
          color={C.neon}
          subtitle={metrics?.totalCost ? `$${metrics.totalCost.toFixed(2)} cost` : undefined}
          tooltip={<div>Total tokens consumed across all AI interactions.</div>}
        />
      </div>

      {/* ── Project Filter ────────────────────────────────────────────── */}
      <GlassPanel accent={C.amber} delay={0.3} style={{ marginBottom: 20, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>
            FILTER BY PROJECT
          </span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={() => setProjectFilter(undefined)}
              style={{
                background: !projectFilter ? `${C.amber}20` : 'transparent',
                border: `1px solid ${!projectFilter ? `${C.amber}50` : C.faint}`,
                color: !projectFilter ? C.amber : C.muted,
                fontFamily: F.mono,
                fontSize: 10,
                padding: '5px 12px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              ALL
            </button>
            {projects?.map((proj) => (
              <button
                key={proj.id}
                onClick={() => setProjectFilter(proj.id)}
                style={{
                  background: projectFilter === proj.id ? `${C.amber}20` : 'transparent',
                  border: `1px solid ${projectFilter === proj.id ? `${C.amber}50` : C.faint}`,
                  color: projectFilter === proj.id ? C.amber : C.muted,
                  fontFamily: F.mono,
                  fontSize: 10,
                  padding: '5px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {proj.name}
              </button>
            ))}
          </div>
        </div>
      </GlassPanel>

      {/* ── Timeline ──────────────────────────────────────────────────── */}
      {grouped.length === 0 ? (
        <EmptyState
          icon="🎯"
          title="No sessions recorded"
          subtitle="Sessions capture development work — time, AI interactions, expertise applied. They auto-link to work items for effort tracking."
        />
      ) : (
        <div style={{ position: 'relative', paddingLeft: 24 }}>
          {/* Timeline line */}
          <div
            style={{
              position: 'absolute',
              left: 10,
              top: 0,
              bottom: 0,
              width: 2,
              background: `linear-gradient(180deg, ${C.amber}40, ${C.amber}10)`,
              borderRadius: 1,
            }}
          />

          {grouped.map(([date, daySessions], gi) => (
            <div key={date} style={{ marginBottom: 24 }}>
              {/* Date header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 10,
                  marginLeft: -14,
                  opacity: 0,
                  animation: `engine-feed-in 0.4s ease ${0.4 + gi * 0.08}s forwards`,
                }}
              >
                {/* Timeline dot */}
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: C.amber,
                    boxShadow: `0 0 8px ${C.amberGlow}`,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12, fontFamily: F.mono, fontWeight: 600, color: C.textSoft, letterSpacing: '0.05em' }}>
                  {date}
                </span>
                <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
                  {daySessions.length} session{daySessions.length !== 1 ? 's' : ''} ·{' '}
                  {Math.round(daySessions.reduce((s, ses) => s + ses.durationMinutes, 0) / 60 * 10) / 10}h
                </span>
              </div>

              {/* Sessions for this date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {daySessions.map((session, si) => (
                  <SessionCard key={session.id} session={session} index={gi * 10 + si} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </EnginePageShell>
  );
}

// ─── SessionCard ────────────────────────────────────────────────────────────

function SessionCard({ session, index }: { session: Session; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const hasAI = !!(session.aiModel || session.aiTokensUsed > 0);
  const accent = hasAI ? C.violet : C.cyan;

  const timeRange = useMemo(() => {
    const start = new Date(session.startTime);
    const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (session.endTime) {
      return `${fmt(start)} — ${fmt(new Date(session.endTime))}`;
    }
    return `${fmt(start)} — ongoing`;
  }, [session]);

  // Duration bar width (max 4 hours = 100%)
  const durationPct = Math.min((session.durationMinutes / 240) * 100, 100);

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        background: C.surface,
        border: `1px solid ${expanded ? `${accent}25` : C.faint}`,
        borderRadius: 10,
        padding: '12px 16px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        opacity: 0,
        animation: `engine-feed-in 0.3s ease ${0.5 + index * 0.04}s forwards`,
        transition: 'border-color 0.2s',
      }}
    >
      {/* Duration bar background */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${durationPct}%`,
          background: `linear-gradient(90deg, ${accent}08, transparent)`,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Icon */}
          <span style={{ fontSize: 16 }}>{hasAI ? '🤖' : '👤'}</span>

          {/* Main info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {session.contributorName || 'Contributor'}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>→</span>
              <span style={{ fontSize: 12, color: C.cyan, fontWeight: 500 }}>
                {session.workItemName || 'Work Item'}
              </span>
              {session.projectName && (
                <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
                  📂 {session.projectName}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 10, fontFamily: F.mono, color: C.dim }}>
              <span>{timeRange}</span>
              <span style={{ color: accent }}>{session.durationMinutes} min</span>
              {hasAI && (
                <>
                  <span style={{ color: C.violet }}>
                    {session.aiModel}
                  </span>
                  <span style={{ color: C.violet }}>
                    {(session.aiTokensUsed ?? 0).toLocaleString()} tokens
                  </span>
                  {(session.aiCost ?? 0) > 0 && (
                    <span style={{ color: C.neon }}>
                      ${(session.aiCost ?? 0).toFixed(3)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Duration display */}
          <EngineTooltip content={<div>Session duration: {session.durationMinutes} minutes ({(session.durationMinutes / 60).toFixed(1)} hours)</div>}>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 16, fontFamily: F.mono, fontWeight: 700, color: accent }}>
                {session.durationMinutes >= 60
                  ? `${(session.durationMinutes / 60).toFixed(1)}h`
                  : `${session.durationMinutes}m`}
              </div>
            </div>
          </EngineTooltip>

          {/* Expand chevron */}
          <span
            style={{
              fontSize: 10,
              color: C.dim,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.2s',
            }}
          >
            ▶
          </span>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: `1px solid ${C.faint}`,
              animation: 'engine-tooltip-in 0.2s ease',
            }}
          >
            {/* Expertise applied */}
            {session.expertiseApplied && session.expertiseApplied.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
                  EXPERTISE APPLIED
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {session.expertiseApplied.map((exp) => (
                    <span
                      key={exp}
                      style={{
                        fontSize: 10,
                        fontFamily: F.mono,
                        color: C.violet,
                        background: `${C.violet}10`,
                        border: `1px solid ${C.violet}20`,
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}
                    >
                      {exp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {session.notes && (
              <div>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 4 }}>
                  NOTES
                </div>
                <div style={{ fontSize: 12, color: C.textSoft, lineHeight: 1.5 }}>
                  {session.notes}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
